import { useQuery } from '@apollo/client';
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { useParams } from 'react-router-dom';
import OPERATIONS_INVOICE_DETAIL from '../graphql/operations-invoice-detail';
import InvoiceAmountCards from './InvoiceAmountCards';
import InvoiceDetailAlert from './InvoiceDetailAlert';
import InvoiceDetailHeader from './InvoiceDetailHeader';
import InvoicePaymentsSection from './InvoicePaymentsSection';
import InvoiceSummaryHeaderTable from './InvoiceSummaryHeaderTable';
import InvoiceTrackingSection from './InvoiceTrackingSection';
import OperationSummarySection from './OperationSummarySection';

const BaseInvoiceDetail = ({
  summaryTableColumns,
  getAmountCards,
  showPayments,
  getTrackingSteps,
  showWarnings,
}) => {
  const { invoiceId } = useParams();
  const { data, loading } = useQuery(OPERATIONS_INVOICE_DETAIL, {
    variables: { invoiceId },
    skip: !invoiceId,
  });
  const invoice = data?.invoice;

  if (loading && !invoice) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }
  if (!invoice) return null;

  const displayPayments = typeof showPayments === 'function' ? showPayments(invoice) : showPayments;

  return (
    <Stack spacing={2} py={2}>
      <InvoiceDetailHeader invoice={invoice} />
      <InvoiceSummaryHeaderTable invoice={invoice} columns={summaryTableColumns} />
      {showWarnings && <InvoiceDetailAlert invoice={invoice} />}
      <InvoiceAmountCards cards={getAmountCards(invoice)} loading={loading} />
      {displayPayments && (
        <InvoicePaymentsSection payments={invoice.receivedPayments || []} />
      )}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <OperationSummarySection
            operation={invoice.operation}
            monthlyRate={invoice.offer?.monthlyRate}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <InvoiceTrackingSection invoice={invoice} getTrackingSteps={getTrackingSteps} />
        </Box>
      </Box>
    </Stack>
  );
};

BaseInvoiceDetail.propTypes = {
  summaryTableColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      render: PropTypes.func.isRequired,
    }),
  ).isRequired,
  getAmountCards: PropTypes.func.isRequired,
  showPayments: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  getTrackingSteps: PropTypes.func,
  showWarnings: PropTypes.bool,
};

BaseInvoiceDetail.defaultProps = {
  showPayments: true,
  getTrackingSteps: () => [],
  showWarnings: true,
};

export default BaseInvoiceDetail;
